import React, { FC, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  PaymentElement,
  useElements,
  useStripe
} from '@stripe/react-stripe-js';
import { StripeError, StripePaymentElement, StripePaymentElementChangeEvent } from '@stripe/stripe-js';

// Shared UI Library Components
import { CustomButton, InfoMessage } from '@ppmcore/seven-ppm-core-shared-components-react';

// Styles
import './add-payment.scss';

// Components
import { StripeFooter } from '../stripe-footer/stripe-footer';

// Store entities
import { createPaymentCard } from '../../../store/payments/payments.thunks';

// Models
import { CPaymentElementOptions } from '../../../constantes/payment-form.constantes';
import { TPaymentElementState } from '../../../types/payments.types';
import { ICurrency } from '../../../interfaces/company.interfaces';

type TAddPaymentProps = {
  currency: ICurrency;
  company_account_id: string | null,
};

export const AddPayment: FC<TAddPaymentProps> = (
  { currency, company_account_id }: TAddPaymentProps
) => {
  const stripe = useStripe();
  const elements = useElements();

  const [isCardComplete, setIsCardComplete] = useState<boolean>(false);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [elementStatus, setElementStatus] = useState<TPaymentElementState>('mounting');

  const dispatch = useDispatch<any>();

  const onReadyHandler = (element: StripePaymentElement): void => {
    setElementStatus('success');
  }

  /**
   * Handles the 'change' event when the Stripe payment element is ready.
   * @param {StripePaymentElementChangeEvent} event - The Stripe payment element.
   * @returns {void}
   */
  const onChangeHandler = (event: StripePaymentElementChangeEvent): void => {
    setIsCardComplete(event.complete);
  }

  const onMountedErrorHandler = (event: { elementType: "payment"; error: StripeError }): void => {
    setElementStatus('error');
    setError(event?.error?.message ?? '');
  }

  const onAddPaymentHandler = async (): Promise<void> => {
    setError('');
    setIsCreating(true);

    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      setIsCreating(false);
      setError('Stripe has not yet loaded');
      return;
    }

    // Trigger form validation and wallet collection
    const { error: submitError } = await elements.submit();

    if (submitError) {
      setIsCreating(false);
      setError(submitError.message ?? '');
      return;
    }

    const { error: createError, paymentMethod } = await stripe.createPaymentMethod({
      elements,
    });

    if (createError) {
      // Show error to your customer (for example, payment details incomplete)
      setIsCreating(false);
      setError(createError.message ?? '');
      return;
    }

    const createdPayload = await dispatch(createPaymentCard(paymentMethod?.id));

    if (createdPayload.payload?.card) {
      elements.getElement('payment')?.clear();
    }
    setIsCreating(false);
  }

  useEffect(() => {
    const computedStyle = getComputedStyle(document.documentElement);
    elements?.update({
      currency: currency.name.toLowerCase(),
      // @ts-ignore
      // ...(company_account_id && { on_behalf_of: company_account_id }),
      appearance: {
        variables: {
          colorBackground: '#ffffff',
          colorText: computedStyle.getPropertyValue('--primary'),
          colorTextPlaceholder: computedStyle.getPropertyValue('--primary-60'),
          colorDanger: computedStyle.getPropertyValue('--red'),
          fontFamily: '"Noto Sans", sans-serif',
        },
        rules: {
          '.Input': {
            borderColor: computedStyle.getPropertyValue('--primary-60'),
            color: computedStyle.getPropertyValue('--primary'),
          }
        }
      }
    });
  }, [currency, company_account_id]);

  return (
    <div className="add-payment">
      <div className="add-payment--header">
        <div className="header-title">
          Add Payment Method
        </div>
        <div className="header-subtitle">
          Please, enter Payment Method Details below.
        </div>
      </div>
      <div className="add-payment--form">
        <PaymentElement options={ CPaymentElementOptions } onReady={ onReadyHandler } onChange={ onChangeHandler }
                        onLoadError={ onMountedErrorHandler }/>
      </div>
      <div className="add-payment--error" hidden={ !error }>
        <InfoMessage infoMessage={ error } type={ 'error' }/>
      </div>
      <div className="add-payment--actions" hidden={ elementStatus !== 'success' }>
        <CustomButton text={ 'Add' } loading={ isCreating } disabled={ !isCardComplete }
                      onClick={ onAddPaymentHandler }/>
      </div>
      <div className="add-payment--footer">
        <StripeFooter/>
      </div>
    </div>
  );
}
